<template>
  <v-dialog :model-value="isApplyAgrochemicalDialogActived" max-width="980px" persistent scrollable>
    <v-card class="dialog-card">
      <v-card-title class="dialog-title">Aplicacion de agroquimicos</v-card-title>
      <v-card-text>
        <v-stepper v-model="step" :items="stepItems" hide-actions>
          <template #[`item.1`]>
            <v-card variant="text">
              <v-card-title class="px-0">Agroquimicos seleccionados</v-card-title>
              <v-card-text>
                <v-form ref="dosageForm" v-model="step1IsValid" validate-on="lazy">
                  <div class="table-wrapper">
                    <v-data-table
                      :items="getItemsToApply"
                      :headers="dosageHeaders"
                      :items-per-page="-1"
                      hide-default-footer>
                      <template #[`item.agrochemical.name`]="{ item }">
                        {{ getRawItem(item).agrochemical?.name || '' }}
                      </template>
                      <template #[`item.dosage`]="{ item }">
                        <v-text-field
                          v-model="getRawItem(item).dosage"
                          type="number"
                          step="0.1"
                          label="Dosis"
                          min="0"
                          variant="outlined"
                          density="compact"
                          hide-details="auto"
                          :rules="[rules.requiredFieldRule, rules.min0]"
                          required
                        />
                      </template>
                    </v-data-table>
                  </div>
                  <v-alert v-if="formError" type="error" variant="tonal" class="mt-4">
                    {{ formError }}
                  </v-alert>
                </v-form>
              </v-card-text>
              <v-card-actions>
                <v-btn color="secondary" variant="text" @click="close">Cancelar</v-btn>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="step1Ready">Confirmar y Continuar</v-btn>
              </v-card-actions>
            </v-card>
          </template>

          <template #[`item.2`]>
            <v-card variant="text">
              <v-card-title class="px-0">Selecciona un lote</v-card-title>
              <v-card-text>
                <v-alert v-if="formError" type="error" variant="tonal" class="mb-4">
                  {{ formError }}
                </v-alert>
                <land-batch-selector v-model="applicationInfo.landBatch" />
              </v-card-text>
              <v-card-actions>
                <v-btn color="secondary" variant="text" @click="close">Cancelar</v-btn>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="step2Ready">Confirmar y Continuar</v-btn>
              </v-card-actions>
            </v-card>
          </template>

          <template #[`item.3`]>
            <v-card variant="text">
              <v-card-title class="px-0">Detalles de aplicacion</v-card-title>
              <v-card-text>
                <v-form ref="detailForm" v-model="step3IsValid" validate-on="lazy">
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="applicationInfo.applicationNumber"
                        type="number"
                        label="Nro. de Aplicacion"
                        min="0"
                        variant="outlined"
                        density="comfortable"
                        :rules="[rules.requiredFieldRule, rules.min0]"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="applicationInfo.applicationType"
                        label="Tipo de Aplicacion"
                        item-title="name"
                        return-object
                        variant="outlined"
                        density="comfortable"
                        :rules="[rules.requiredFieldRule]"
                        :items="getAgrochemicalApplicationTypes"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="applicationInfo.haAmount"
                        label="Cantidad de Hectareas"
                        type="number"
                        min="0"
                        variant="outlined"
                        density="comfortable"
                        :rules="[rules.requiredFieldRule, rules.min0]"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="applicationInfo.applicationDate"
                        label="Fecha de Aplicacion"
                        type="date"
                        variant="outlined"
                        density="comfortable"
                        :rules="[rules.requiredFieldRule]"
                      />
                    </v-col>
                  </v-row>
                </v-form>
              </v-card-text>
              <v-card-actions>
                <v-btn color="secondary" variant="text" @click="close">Cancelar</v-btn>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="step3Ready">Confirmar y Guardar</v-btn>
              </v-card-actions>
            </v-card>
          </template>
        </v-stepper>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
<script>
import { mapGetters } from 'vuex'
import LandBatchSelector from '../Common/LandBatchSelector'

const getDefaultApplicationInfo = () => ({
  applicationDate: '',
  haAmount: '',
  applicationNumber: '',
  items: [],
  landBatch: {id: null},
  applicationType: ''
})

export default {
  name: 'ApplyAgrochemicalsForm',
  components: {
    LandBatchSelector
  },
  data: () => ({
    rules: {
      requiredFieldRule: v => !!v || 'Campo requerido.',
      min0: v => (v > 0) || 'Debe ingresar un valor mayor que 0'
    },
    dosageHeaders: [
      { title: 'Agroquimico', key: 'agrochemical.name' },
      { title: 'Dosis', key: 'dosage', sortable: false }
    ],
    stepItems: [
      'Aplicacion y dosis',
      'Seleccion de lote',
      'Detalles de aplicacion'
    ],
    step1IsValid: true,
    step2IsValid: true,
    step3IsValid: true,
    applicationInfo: getDefaultApplicationInfo(),
    step: 1,
    formError: ''
  }),

  created () {
    this.$store.dispatch('typesModule/getAgrochemicalApplicationTypes')
  },
  computed: {
    ...mapGetters('agrochemicalModule', ['isApplyAgrochemicalDialogActived', 'getItemsToApply']),
    ...mapGetters('typesModule', ['getAgrochemicalApplicationTypes'])
  },

  methods: {
    getRawItem (item) {
      return item.raw || item
    },

    close () {
      this.applicationInfo = getDefaultApplicationInfo()
      if (this.$refs.dosageForm) {
        this.$refs.dosageForm.resetValidation()
      }
      if (this.$refs.detailForm) {
        this.$refs.detailForm.resetValidation()
      }
      this.step = 1
      this.step1IsValid = true
      this.step2IsValid = true
      this.step3IsValid = true
      this.formError = ''
      this.$store.dispatch('agrochemicalModule/setSelectedApplicationItem', [])
      this.$store.dispatch('agrochemicalModule/setApplyAgrochemicalsDialogActived', false)
    },

    async step1Ready () {
      if (await this.validateStep1()) {
        this.formError = ''
        this.step = 2
      } else {
        this.formError = 'Debe ingresar valores para las dosis de todos los agroquimicos seleccionados.'
      }
    },

    async validateStep1 () {
      const validation = await this.$refs.dosageForm.validate()
      this.step1IsValid = validation.valid
      if (this.step1IsValid) {
        this.applicationInfo.items = []
        for (const item of this.$store.getters['agrochemicalModule/getItemsToApply']) {
          if (item.dosage > 0) {
            this.applicationInfo.items.push({agrochemical: item.agrochemical, dosage: item.dosage})
          } else {
            this.step1IsValid = false
            break
          }
        }
      }
      return this.step1IsValid
    },

    step2Ready () {
      if (this.validateStep2()) {
        this.formError = ''
        this.step = 3
      } else {
        this.formError = 'Debe seleccionar un lote.'
      }
    },

    validateStep2 () {
      this.step2IsValid = !!(this.applicationInfo.landBatch && Number(this.applicationInfo.landBatch.id) > 0)
      return this.step2IsValid
    },

    async step3Ready () {
      if (await this.validateStep3()) {
        if ((await this.validateStep1()) && this.validateStep2()) {
          await this.saveApplication(this.applicationInfo)
          this.close()
        } else {
          this.formError = 'Hay pasos incompletos. Por favor, complete la informacion correctamente y vuelva a intentarlo.'
        }
      }
    },

    async validateStep3 () {
      const validation = await this.$refs.detailForm.validate()
      this.step3IsValid = validation.valid
      return this.step3IsValid
    },

    async saveApplication (application) {
      try {
        await this.$store.dispatch('agrochemicalModule/saveAgroApplication', application)
      } catch (error) {
        console.log('Error saving application.', error)
      }
    }
  }
}
</script>
